import React, {useEffect, useState} from 'react'
import {Box, CircularProgress, Chip, Typography} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import DiseasesSection from 'src/pages-new/profile/sections/DiseasesSection'
import {STATUS} from 'src/constants/constants'
import {NEUTRAL_SCALE, CARD_BORDER_COLOR, CARD_BORDER_RADIUS} from 'src/theme-new/tokens'
import apiClient from 'src/services/apiClient'
import {OPTIONAL_PRIVACY_CONSENTS, isOptionalConsentVisible} from 'src/constants/privacyConsents'

interface AnagraficaTabProps {
  patientId: string
  profileConfig?: Record<string, any>
}

const SERVICE_CONF_LABELS: Record<string, string> = {
  EXTRA: 'Extra',
  SMART: 'Smart'
}

// Same "Attivo" wording as the single-option CheckboxGroupNew in OthersDataSection.tsx —
// drugDeliveryService is stored as an array there (e.g. ['true']), not a boolean, so a
// plain boolean is handled defensively too in case getPatientsUserData ever returns one.
function drugDeliveryLabel(value: any): string | undefined {
  if (Array.isArray(value)) return value.includes('true') ? 'Attivo' : 'Non attivo'
  if (typeof value === 'boolean') return value ? 'Attivo' : 'Non attivo'
  return undefined
}

function hasVisibleAddressField(addressConfig?: Record<string, {hidden?: boolean}>): boolean {
  if (!addressConfig) return false
  return Object.values(addressConfig).some(cfg => cfg && !cfg.hidden)
}

// Indirizzo resta un'unica riga (non 8 campi separati come ContactDataSection.tsx), ma il
// contenuto della riga rispetta comunque l'hidden di ciascun sotto-campo — "Stato"/"Regione"
// (mancanti prima) compaiono solo se configurati, stessa convenzione "nascosto finché non
// configurato" di ContactDataSection.tsx. Formato: "via, civico, località, cap,
// città (provincia), regione, stato" — città e provincia si combinano tra parentesi solo
// quando sono entrambe configurate e visibili, altrimenti compare solo quella presente.
function buildAddressLine(address: any, addressConfig?: Record<string, {hidden?: boolean}>): string | undefined {
  if (!address) return undefined
  const isVisible = (key: string): boolean => {
    const cfg = addressConfig?.[key]
    return Boolean(cfg && !cfg.hidden)
  }
  const street = address.street ?? address.address
  const city = isVisible('city') ? address.city : undefined
  const province = isVisible('province') ? address.province : undefined
  const cityProvince = city && province ? `${city} (${province})` : (city || province)
  return [
    isVisible('address') ? street : undefined,
    isVisible('streetNumber') ? address.streetNumber : undefined,
    isVisible('locality') ? address.locality : undefined,
    isVisible('postalCode') ? (address.postalCode ?? address.cap) : undefined,
    cityProvince,
    isVisible('region') ? address.region : undefined,
    isVisible('country') ? 'Italia' : undefined
  ].filter(Boolean).join(', ') || undefined
}

const REMINDER_LABELS: Record<string, string> = {
  SMS: 'SMS',
  EMAIL: 'Email',
  CALL: 'Chiamata',
  // therapyReminderTypes (a distinct field from remindTherapyExpiryTypes) uses 'VOICE'
  // rather than 'CALL' for the same "Chiamata" option — see THERAPY_REMINDER_OPTIONS in
  // src/pages-new/profile/sections/ContactDataSection.tsx.
  VOICE: 'Chiamata',
  PUSH: 'Push',
  PHONE_NUMBER: 'Telefono',
  CELL_PHONE_NUMBER: 'Cellulare',
  FAX_NUMBER: 'Fax'
}

function mapReminderCodes(codes?: string[]): string[] {
  return (codes ?? []).map(code => REMINDER_LABELS[code] ?? code)
}

// Mappatura identica a `SEX_LABELS` in src/pages-new/patient/tabs/caregivers/CaregiverDetailDialogNew.tsx
const SEX_LABELS: Record<string, string> = {
  MALE: 'Maschio',
  FEMALE: 'Femmina',
  NOT_DECLARED: 'Non dichiarato'
}

function sexLabel(value?: string): string | undefined {
  return value ? SEX_LABELS[value] ?? value : undefined
}

function formatPerson(person: any): string | undefined {
  if (!person) return undefined
  const name = `${person.title ?? ''} ${person.firstName ?? ''} ${person.lastName ?? ''}`.replace(/\s+/g, ' ').trim()
  return name || undefined
}

function Field({label, value}: {label: string; value: React.ReactNode}): JSX.Element {
  return (
    <Box>
      <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.4}}>
        {label}
      </Typography>
      <Typography variant="body2">{value ?? '—'}</Typography>
    </Box>
  )
}

function SectionTitle({children}: {children: string}): JSX.Element {
  return (
    <Typography
      variant="h2"
      sx={{fontSize: 13, fontWeight: 700, color: NEUTRAL_SCALE[700], mb: 2, textTransform: 'uppercase', letterSpacing: 0.5}}
    >
      {children}
    </Typography>
  )
}

function TwoColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
        gap: 2,
        mb: 1
      }}
    >
      {children}
    </Box>
  )
}

function ThreeColGrid({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 2}}>
      {children}
    </Box>
  )
}

function SectionLabel({children}: {children: React.ReactNode}): JSX.Element {
  return (
    <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 1, textTransform: 'uppercase', letterSpacing: 0.4}}>
      {children}
    </Typography>
  )
}

function InfoRow({label, value}: {label: string; value?: string | null}): JSX.Element {
  return (
    <Box sx={{display: 'flex', gap: 1, alignItems: 'baseline'}}>
      <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500], minWidth: 80, flexShrink: 0}}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{color: value ? 'text.primary' : NEUTRAL_SCALE[300], wordBreak: 'break-word'}}>
        {value || '—'}
      </Typography>
    </Box>
  )
}

// Stessa costruzione usata per i centri medici nel Profilo (UserDataSection.tsx)
function buildMedicalCenterAddressLine(center: any): string {
  return [
    [center.address, center.streetNumber].filter(Boolean).join(' '),
    center.postalCode,
    center.city,
    center.province
  ].filter(Boolean).join(', ')
}

// Le qualifiche arrivano già risolte come oggetti da getPatientsUserData (psp-backend),
// ma si gestisce anche il caso stringa per coerenza con la mappatura usata nel Profilo.
function formatQualifications(qualifications?: any[]): string {
  return (qualifications ?? [])
    .map((q: any) => (typeof q === 'string' ? q : q?.name))
    .filter(Boolean)
    .join(', ')
}

// Stessa card (bordo, nome in evidenza, righe InfoRow, placeholder se assente) usata per
// Caregiver in PersonField del Profilo dell'utente loggato (ContactDataSection.tsx) — qui
// in sola lettura, senza le azioni di ricerca/rimozione.
function PersonCard({label, name, rows, emptyLabel}: {
  label: string
  name?: string
  rows: {label: string; value?: string}[]
  emptyLabel: string
}): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75, display: 'block'}}>
        {label}
      </Typography>
      <Box
        sx={{
          border: `1px solid ${NEUTRAL_SCALE[200]}`,
          borderRadius: '12px',
          p: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          minHeight: 84,
          ...(name ? {} : {justifyContent: 'center', alignItems: 'center'})
        }}
      >
        {name ? (
          <>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
              {name}
            </Typography>
            {rows.map(row => <InfoRow key={row.label} label={row.label} value={row.value} />)}
          </>
        ) : (
          <Typography variant="body2" sx={{color: NEUTRAL_SCALE[500], textAlign: 'center'}}>
            {emptyLabel}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

function computeAge(birthDate?: string): string {
  if (!birthDate) return '—'
  const birth = new Date(birthDate)
  if (isNaN(birth.getTime())) return '—'
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return `${age} anni`
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return dateStr
  return d.toLocaleDateString('it-IT')
}

export default function AnagraficaTab({patientId, profileConfig}: AnagraficaTabProps): JSX.Element {
  const [patientData, setPatientData] = useState<any>(null)
  const [caregiver, setCaregiver] = useState<any>(undefined)
  const [delegate1, setDelegate1] = useState<any>(undefined)
  const [delegate2, setDelegate2] = useState<any>(undefined)
  const [status, setStatus] = useState<string>(STATUS.PENDING)

  useEffect(() => {
    apiClient.getPatientsUserData(patientId, setStatus)
      .then((data: any) => {
        if (!data) return
        setPatientData(data)

        // Caregiver: risolto dai caregiver del paziente visualizzato, non da quelli
        // dell'utente loggato — /caregivers/ filtra sull'id di chi chiama.
        apiClient.getPatientsCaregivers(patientId, () => undefined)
          .then((list: any) => setCaregiver((list ?? []).find((el: any) => String(el._id) === String(data.caregiverId))))
          .catch(() => setCaregiver(undefined))

        // Delegati: Delegato 1/2 sono, per convenzione di prodotto, il primo e il
        // secondo id del pool consentito (delegateDoctorIds) — delegateId_1/2 non
        // vengono valorizzati da nessun flusso e non si usano per il matching.
        const [delegateDoctorId1, delegateDoctorId2] = data.delegateDoctorIds ?? []
        const requestedIds = [delegateDoctorId1, delegateDoctorId2].filter(Boolean).map(String)

        if (requestedIds.length > 0) {
          apiClient.getDoctorsSearch([{property: 'ids', value: requestedIds.join(',')}], () => undefined)
            .then((docs: any) => {
              setDelegate1((docs ?? []).find((d: any) => String(d._id) === String(delegateDoctorId1)))
              setDelegate2(delegateDoctorId2 ? (docs ?? []).find((d: any) => String(d._id) === String(delegateDoctorId2)) : undefined)
            })
            .catch(() => {
              setDelegate1(undefined)
              setDelegate2(undefined)
            })
        } else {
          setDelegate1(undefined)
          setDelegate2(undefined)
        }
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [patientId])

  if (status === STATUS.PENDING) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (status === STATUS.REJECTED || !patientData) {
    return (
      <Box sx={{py: 4, textAlign: 'center'}}>
        <Typography variant="body2" color="error">
          Impossibile caricare i dati del paziente.
        </Typography>
      </Box>
    )
  }

  const p = patientData

  // p.privacyConsents è un array di codici (es. ['ENROLLMENT', 'PRIVACY_PHONE_SURVEY']), non un oggetto
  const privacyConsents: string[] = p.privacyConsents ?? []
  const privacyItems = [
    {key: 'ENROLLMENT', label: 'Per l\'adesione al Programma', value: privacyConsents.includes('ENROLLMENT')},
    {
      key: 'PRIVACY_PHONE_SURVEY',
      label: 'Per rilevazione telefonica finalizzata alla valutazione del gradimento dei servizi',
      value: privacyConsents.includes('PRIVACY_PHONE_SURVEY')
    },
    {
      key: 'PRIVACY_ENROLLMENT_APP_WEB',
      label: 'Per la registrazione al portale/app',
      value: privacyConsents.includes('PRIVACY_ENROLLMENT_APP_WEB')
    }
  ].filter(item => !profileConfig?.privacyConsents?.[item.key]?.hidden)
    .concat(OPTIONAL_PRIVACY_CONSENTS
      .filter(({key}) => isOptionalConsentVisible(profileConfig?.privacyConsents, key))
      .map(({key, label}) => ({key, label, value: privacyConsents.includes(key)})))

  const booleanLabel = (value: any): string | undefined => (typeof value === 'boolean' ? (value ? 'Sì' : 'No') : undefined)

  const communicationMethods = mapReminderCodes(p.preferredContacts)
  const contactMethods = mapReminderCodes(p.preferredContactMethods)
  const reminderTypes = mapReminderCodes(p.remindTherapyExpiryTypes)
  const therapyReminderTypes = mapReminderCodes(p.therapyReminderTypes)

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      {/* Dati anagrafici */}
      <Card>
        <SectionTitle>Dati anagrafici</SectionTitle>
        <TwoColGrid>
          {!profileConfig?.title?.hidden && <Field label="Titolo" value={p.title} />}
          {!profileConfig?.lastName?.hidden && <Field label="Cognome" value={p.lastName} />}
          {!profileConfig?.firstName?.hidden && <Field label="Nome" value={p.firstName} />}
          {!profileConfig?.code?.hidden && <Field label="Codice Paziente" value={p.code} />}
          {profileConfig?.fiscalCode && !profileConfig?.fiscalCode?.hidden && (
            <Field label="Codice Fiscale" value={p.fiscalCode} />
          )}
          {!profileConfig?.sex?.hidden && <Field label="Sesso" value={sexLabel(p.sex ?? p.gender)} />}
          {!profileConfig?.nationality?.hidden && <Field label="Nazionalità" value={p.nationality} />}
          {!profileConfig?.birthDate?.hidden && <Field label="Data di Nascita" value={formatDate(p.birthDate)} />}
          {!profileConfig?.age?.hidden && <Field label="Età" value={computeAge(p.birthDate)} />}
        </TwoColGrid>
      </Card>

      {/* Dati di contatto */}
      <Card>
        <SectionTitle>Dati di contatto</SectionTitle>

        {profileConfig?.preferredContacts && !profileConfig?.preferredContacts?.hidden && communicationMethods.length > 0 && (
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4}}>
              Metodi di comunicazione preferiti
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {communicationMethods.map((method: string) => (
                <Chip key={method} label={method} size="small" />
              ))}
            </Box>
          </Box>
        )}

        {profileConfig?.preferredContactMethods && !profileConfig?.preferredContactMethods?.hidden && contactMethods.length > 0 && (
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4}}>
              Metodi di contatto
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {contactMethods.map((method: string) => (
                <Chip key={method} label={method} size="small" />
              ))}
            </Box>
          </Box>
        )}

        {profileConfig?.remindTherapyExpiryTypes && !profileConfig?.remindTherapyExpiryTypes?.hidden && reminderTypes.length > 0 && (
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4}}>
              Remind scadenza Piano Terapeutico
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {reminderTypes.map((method: string) => (
                <Chip key={method} label={method} size="small" />
              ))}
            </Box>
          </Box>
        )}

        {profileConfig?.therapyReminderTypes && !profileConfig?.therapyReminderTypes?.hidden && therapyReminderTypes.length > 0 && (
          <Box sx={{mb: 2}}>
            <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4}}>
              Remind assunzione Terapia
            </Typography>
            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
              {therapyReminderTypes.map((method: string) => (
                <Chip key={method} label={method} size="small" />
              ))}
            </Box>
          </Box>
        )}

        <TwoColGrid>
          {profileConfig?.email && !profileConfig?.email?.hidden && <Field label="Email" value={p.email} />}
          <Box />
          {profileConfig?.phoneNumber && !profileConfig?.phoneNumber?.hidden && <Field label="Telefono" value={p.phoneNumber} />}
          {profileConfig?.cellPhoneNumber && !profileConfig?.cellPhoneNumber?.hidden && (
            <Field label="Cellulare" value={p.cellPhoneNumber} />
          )}
          {profileConfig?.faxNumber && !profileConfig?.faxNumber?.hidden && <Field label="Fax" value={p.faxNumber} />}
          {profileConfig?.skypeId && !profileConfig?.skypeId?.hidden && (
            <Field label="Contatto Skype" value={p.skypeId} />
          )}
        </TwoColGrid>

        {profileConfig?.caregiverId && !profileConfig?.caregiverId?.hidden && (
          <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 2, mb: 2}}>
            <PersonCard
              label="Caregiver"
              name={formatPerson(caregiver)}
              rows={[
                {label: 'Telefono', value: caregiver?.cellPhoneNumber || caregiver?.phoneNumber},
                {label: 'Email', value: caregiver?.email}
              ]}
              emptyLabel="Nessun caregiver selezionato"
            />
          </Box>
        )}

        {hasVisibleAddressField(profileConfig?.address) && (
          <Field label="Indirizzo" value={buildAddressLine(p.address, profileConfig?.address)} />
        )}
      </Card>

      {/* Patologie */}
      <DiseasesSection fields={{diseases: profileConfig?.diseases}} data={p} />

      {/* Rete di assistenza: medico prescrittore, centro medico, delegati e operatori
          sanitari — separati da Dati anagrafici/contatto per leggibilità (non sono dati
          del paziente in sé, ma la sua rete di cura) */}
      {(
        !profileConfig?.prescribingDoctorId?.hidden ||
        !profileConfig?.medicalCenterId?.hidden ||
        (profileConfig?.delegateId_1 && !profileConfig?.delegateId_1?.hidden) ||
        (profileConfig?.delegateId_2 && !profileConfig?.delegateId_2?.hidden) ||
        !profileConfig?.operatorsIds?.hidden
      ) && (
        <Card>
          <SectionTitle>Rete di assistenza</SectionTitle>

          {!profileConfig?.prescribingDoctorId?.hidden && (
            <Box sx={{mb: 3}}>
              <Field
                label="Medico prescrittore"
                value={p.prescribingDoctor
                  ? `${p.prescribingDoctor.firstName ?? ''} ${p.prescribingDoctor.lastName ?? ''}`.trim() || p.prescribingDoctor
                  : undefined
                }
              />
            </Box>
          )}

          {!profileConfig?.medicalCenterId?.hidden && (
            <Box sx={{mb: 3}}>
              <SectionLabel>Centro Medico</SectionLabel>
              {(p.medicalCenters ?? []).length === 0 ? (
                <EmptyState title="Nessun risultato" description="Nessun centro medico registrato" />
              ) : (
                <ThreeColGrid>
                  {p.medicalCenters.map((center: any) => (
                    <Box
                      key={center._id}
                      sx={{
                        border: `1px solid ${CARD_BORDER_COLOR}`,
                        borderRadius: CARD_BORDER_RADIUS,
                        p: 2,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75
                      }}
                    >
                      <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
                        {center.name || '—'}
                      </Typography>
                      <InfoRow label="Indirizzo" value={buildMedicalCenterAddressLine(center) || undefined} />
                      <InfoRow label="Telefono" value={center.phoneNumber} />
                      <InfoRow label="Email" value={center.email} />
                      <InfoRow label="Sito web" value={center.website} />
                    </Box>
                  ))}
                </ThreeColGrid>
              )}
            </Box>
          )}

          {(
            (profileConfig?.delegateId_1 && !profileConfig?.delegateId_1?.hidden) ||
            (profileConfig?.delegateId_2 && !profileConfig?.delegateId_2?.hidden)
          ) && (
            <Box sx={{mb: 3}}>
              <SectionLabel>Delegati</SectionLabel>
              <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 2}}>
                {profileConfig?.delegateId_1 && !profileConfig?.delegateId_1?.hidden && (
                  <PersonCard
                    label="Delegato 1"
                    name={formatPerson(delegate1)}
                    rows={[{label: 'Email', value: delegate1?.email}]}
                    emptyLabel="Nessun delegato selezionato"
                  />
                )}
                {profileConfig?.delegateId_2 && !profileConfig?.delegateId_2?.hidden && (
                  <PersonCard
                    label="Delegato 2"
                    name={formatPerson(delegate2)}
                    rows={[{label: 'Email', value: delegate2?.email}]}
                    emptyLabel="Nessun delegato selezionato"
                  />
                )}
              </Box>
            </Box>
          )}

          {!profileConfig?.operatorsIds?.hidden && (
            <Box>
              <SectionLabel>Operatori Sanitari</SectionLabel>
              {(p.operators ?? []).length === 0 ? (
                <EmptyState title="Nessun risultato" description="Nessun operatore sanitario registrato" />
              ) : (
                <ThreeColGrid>
                  {p.operators.map((operator: any) => (
                    <Box
                      key={operator._id}
                      sx={{
                        border: `1px solid ${CARD_BORDER_COLOR}`,
                        borderRadius: CARD_BORDER_RADIUS,
                        p: 2,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75
                      }}
                    >
                      <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 0.25}}>
                        {`${operator.firstName ?? ''} ${operator.lastName ?? ''}`.trim() || '—'}
                      </Typography>
                      <InfoRow label="Qualifiche" value={formatQualifications(operator.qualifications) || undefined} />
                      <InfoRow label="Telefono" value={operator.cellPhoneNumber ?? operator.phoneNumber} />
                      <InfoRow label="Email" value={operator.email} />
                    </Box>
                  ))}
                </ThreeColGrid>
              )}
            </Box>
          )}
        </Card>
      )}

      {/* Dati adesione al programma (date + consenso al trattamento, stessa card) */}
      <Card>
        <SectionTitle>Dati adesione al programma</SectionTitle>
        <TwoColGrid>
          {!profileConfig?.enrollmentDate?.hidden && (
            <Field label="Data di Adesione" value={formatDate(p.enrollmentDate)} />
          )}
          {!profileConfig?.receiveModuleDate?.hidden && (
            <Field label="Data ricezione modulo" value={formatDate(p.receiveModuleDate)} />
          )}
        </TwoColGrid>

        {privacyItems.length > 0 && (
          <>
            <Typography sx={{fontSize: 11, color: NEUTRAL_SCALE[500], mt: 2, mb: 1, textTransform: 'uppercase', letterSpacing: 0.4}}>
              Consenso al trattamento
            </Typography>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              {privacyItems.map(item => (
                <Box key={item.key} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  {item.value ? (
                    <CheckCircleOutlineIcon sx={{color: '#16A34A', fontSize: 18}} />
                  ) : (
                    <CancelOutlinedIcon sx={{color: '#DC2626', fontSize: 18}} />
                  )}
                  <Typography variant="body2">{item.label}</Typography>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Card>

      {/* Servizi attivi: mds/testDBSService (prima in "Dati adesione al programma") insieme a
          serviceConf/drugDeliveryService (prima "Configurazione servizi") — un'unica card per
          tutti i servizi configurati sul paziente, in fondo alla pagina */}
      {(
        !profileConfig?.mds?.hidden ||
        !profileConfig?.testDBSService?.hidden ||
        !profileConfig?.serviceConf?.hidden ||
        !profileConfig?.drugDeliveryService?.hidden
      ) && (
        <Card>
          <SectionTitle>Servizi attivi</SectionTitle>
          <TwoColGrid>
            {!profileConfig?.mds?.hidden && <Field label="Medico del Servizio" value={booleanLabel(p.mds)} />}
            {!profileConfig?.testDBSService?.hidden && (
              <Field label="Servizio test DBS" value={booleanLabel(p.testDBSService)} />
            )}
            {!profileConfig?.serviceConf?.hidden && (
              <Field label="Configurazione di servizio" value={SERVICE_CONF_LABELS[p.serviceConf] ?? p.serviceConf} />
            )}
            {!profileConfig?.drugDeliveryService?.hidden && (
              <Field label="Servizio consegna farmaco" value={drugDeliveryLabel(p.drugDeliveryService)} />
            )}
          </TwoColGrid>
        </Card>
      )}
    </Box>
  )
}
